// ----------------------
// GDPR-SAFE TYPE DEFINITIONS
// ----------------------
interface UserPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface SessionData {
  id: string;
  startTime: string;
  device: string;
  browser: string;
  os: string;
  screenResolution: string;
  language: string;
  timezone: string;
  country: string | null;
}

interface PlatformAnalyticsSummary {
  totalUsers: number;
  analyticsAccepted: number;
  analyticsDeclined: number;
  acceptanceRate: number;
}

interface VisitorStats {
  totalVisitors: number;
  dailyAverage: number;
  weeklyGrowth: string;
  monthlyActive: number;
}

interface CountryData {
  country: string;
  users: number;
  percentage: number;
}

interface DeviceBreakdown {
  desktop: number;
  mobile: number;
  tablet: number;
}

interface DemographicsData {
  topCountries: CountryData[];
  devices: DeviceBreakdown;
}

interface PlatformAnalytics {
  summary: PlatformAnalyticsSummary;
  aggregatedData: {
    visitorStats: VisitorStats;
    demographics: DemographicsData;
  };
  updatedAt: string;
}

// ----------------------
// SIMPLIFIED ANALYTICS COLLECTOR (Consent Only)
// ----------------------

class AnalyticsCollector {
  private analyticsData: SessionData | null = null;
  private isInitialized = false;
  private hasSentConsentDecision = false;
  private clientId: string;
  
  // Platform analytics aggregation
  private static platformAnalytics: PlatformAnalytics = {
    summary: {
      totalUsers: 0,
      analyticsAccepted: 0,
      analyticsDeclined: 0,
      acceptanceRate: 0
    },
    aggregatedData: {
      visitorStats: {
        totalVisitors: 0,
        dailyAverage: 0,
        weeklyGrowth: '0%',
        monthlyActive: 0
      },
      demographics: {
        topCountries: [],
        devices: {
          desktop: 0,
          mobile: 0,
          tablet: 0
        }
      }
    },
    updatedAt: new Date().toISOString()
  };

  constructor() {
    // Generate or load client ID
    this.clientId = this.getOrCreateClientId();
    
    // Load platform analytics - safe for SSR
    this.loadPlatformAnalytics();
  }

  // ----------------------
  // Client ID Management
  // ----------------------
  
  private getOrCreateClientId(): string {
    if (typeof window === 'undefined') return 'server-side';
    
    try {
      const localClientId = localStorage.getItem('clientId');
      if (!localClientId) {
        const clientId = 'client_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
        localStorage.setItem('clientId', clientId);
        return clientId;
      }
      return localClientId;
    } catch (err) {
      console.warn('Could not access localStorage:', err);
      return 'anonymous_' + Math.random().toString(36).substring(2, 11);
    }
  }

  public getClientId(): string {
    return this.clientId;
  }

  // ----------------------
  // Platform Analytics Methods
  // ----------------------
  
  private loadPlatformAnalytics(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const saved = localStorage.getItem('platformAnalytics');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          AnalyticsCollector.platformAnalytics = {
            ...AnalyticsCollector.platformAnalytics,
            ...parsed,
            aggregatedData: {
              ...AnalyticsCollector.platformAnalytics.aggregatedData,
              ...(parsed.aggregatedData || {})
            }
          };
        }
      }
    } catch (err) {
      console.warn('Could not load platform analytics:', err);
      this.resetPlatformAnalytics();
    }
  }

  private updatePlatformAnalytics(userAccepted: boolean, forceUpdate: boolean = false): void {
    if (!forceUpdate && this.hasSentConsentDecision) {
      return;
    }
    
    AnalyticsCollector.platformAnalytics.summary.totalUsers++;
    
    if (userAccepted) {
      AnalyticsCollector.platformAnalytics.summary.analyticsAccepted++;
    } else {
      AnalyticsCollector.platformAnalytics.summary.analyticsDeclined++;
    }
    
    const { analyticsAccepted, totalUsers } = AnalyticsCollector.platformAnalytics.summary;
    AnalyticsCollector.platformAnalytics.summary.acceptanceRate = 
      totalUsers > 0 ? Math.round((analyticsAccepted / totalUsers) * 100) : 0;
    
    AnalyticsCollector.platformAnalytics.updatedAt = new Date().toISOString();
    // this.savePlatformAnalytics();
    
    if (!forceUpdate) {
      this.hasSentConsentDecision = true;
    }
  }

  private updateVisitorStats(): void {
    if (!this.analyticsData || typeof window === 'undefined') return;
    
    const stats = AnalyticsCollector.platformAnalytics.aggregatedData.visitorStats;
    stats.totalVisitors = (stats.totalVisitors || 0) + 1;
    stats.dailyAverage = Math.round(stats.totalVisitors / 30);
    stats.weeklyGrowth = '+12%';
    stats.monthlyActive = Math.round(stats.totalVisitors * 0.8);
    
    this.updateDemographics();
    
    AnalyticsCollector.platformAnalytics.updatedAt = new Date().toISOString();
    // this.savePlatformAnalytics();
  }

  private updateDemographics(): void {
    if (!this.analyticsData || typeof window === 'undefined') return;
    
    const demographics = AnalyticsCollector.platformAnalytics.aggregatedData.demographics;
    const session = this.analyticsData;
    
    if (!demographics.devices) {
      demographics.devices = { desktop: 0, mobile: 0, tablet: 0 };
    }
    
    const device = session.device.toLowerCase();
    if (device === 'desktop' || device === 'mobile' || device === 'tablet') {
      const deviceKey = device as keyof DeviceBreakdown;
      demographics.devices[deviceKey] = (demographics.devices[deviceKey] || 0) + 1;
      
      const totalDevices = Object.values(demographics.devices).reduce((a, b) => a + b, 0);
      if (totalDevices > 0) {
        Object.keys(demographics.devices).forEach(key => {
          const deviceKey = key as keyof DeviceBreakdown;
          demographics.devices[deviceKey] = Math.round((demographics.devices[deviceKey] / totalDevices) * 100);
        });
      }
    }
    
    if (session.country) {
      if (!demographics.topCountries) {
        demographics.topCountries = [];
      }
      
      const existingCountry = demographics.topCountries.find(c => c.country === session.country);
      
      if (existingCountry) {
        existingCountry.users++;
      } else if (demographics.topCountries.length < 5) {
        demographics.topCountries.push({
          country: session.country,
          users: 1,
          percentage: 20
        });
      }
      
      demographics.topCountries.sort((a, b) => b.users - a.users);
      demographics.topCountries = demographics.topCountries.slice(0, 5);
      
      const totalUsers = demographics.topCountries.reduce((sum, c) => sum + c.users, 0);
      if (totalUsers > 0) {
        demographics.topCountries.forEach(country => {
          country.percentage = Math.round((country.users / totalUsers) * 100);
        });
      }
    }
  }

  // ----------------------
  // Public method to get platform analytics
  // ----------------------
  
  public static getPlatformAnalytics(): PlatformAnalytics {
    return AnalyticsCollector.platformAnalytics;
  }

  // ----------------------
  // Session Data Collection
  // ----------------------
  
  private collectSessionData(): SessionData {
    if (typeof window === 'undefined') {
      throw new Error('Cannot collect session data on server side');
    }
    
    const now = new Date();
    const sessionId = this.generateSessionId();

    return {
      id: sessionId,
      startTime: now.toISOString(),
      device: this.detectDevice(),
      browser: this.detectBrowser(),
      os: this.detectOS(),
      screenResolution: this.getScreenResolution(),
      language: navigator.language || 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      country: this.getCountryFromIP()
    };
  }

  // ----------------------
  // Public API Methods for Consent Handling
  // ----------------------
  
  public handleAcceptAnalytics(marketing: boolean = false): void {
    if (typeof window === 'undefined') {
      console.warn('Cannot handle consent on server side');
      return;
    }
    
    const userPreferences: UserPreferences = {
      necessary: true,
      analytics: true,
      marketing: marketing,
      functional: true
    };
    
    // Collect session data
    this.analyticsData = this.collectSessionData();
    
    // Update platform analytics
    this.updatePlatformAnalytics(true);
    this.updateVisitorStats();

    // Send consent decision to backend
    this.sendConsentDecisionToBackend({
      action: 'accept',
      timestamp: new Date().toISOString(),
      preferences: userPreferences,
      sessionId: this.analyticsData.id,
      sessionData: this.analyticsData  // Include full session data
    });
    
    // Save preferences to localStorage
    // this.saveUserPreferences(userPreferences);
    
    this.isInitialized = true;
    console.log('Analytics accepted and session data collected');
  }
  
  public handleDeclineAnalytics(): void {
    if (typeof window === 'undefined') {
      console.warn('Cannot handle consent on server side');
      return;
    }
    
    const userPreferences: UserPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    // Collect session data even for declined users (for statistics)
    this.analyticsData = this.collectSessionData();
    
    // Update platform analytics
    this.updatePlatformAnalytics(false);
    this.updateVisitorStats();

    // Send consent decision to backend
    this.sendConsentDecisionToBackend({
      action: 'decline',
      timestamp: new Date().toISOString(),
      preferences: userPreferences,
      sessionId: this.analyticsData.id,
      sessionData: this.analyticsData  // Include full session data
    });
    
    // Save preferences to localStorage
    // this.saveUserPreferences(userPreferences);
    
    this.isInitialized = false; // Analytics not enabled for declined users
    console.log('Analytics declined, only consent recorded');
  }
  
  public handleSavePreferences(preferences: UserPreferences): void {
    if (typeof window === 'undefined') {
      console.warn('Cannot handle preferences on server side');
      return;
    }
    
    // Collect session data
    if (!this.analyticsData) {
      this.analyticsData = this.collectSessionData();
    }
    
    // Update platform analytics
    this.updatePlatformAnalytics(preferences.analytics);
    this.updateVisitorStats();

    // Send preferences to backend
    this.sendConsentDecisionToBackend({
      action: 'customize',
      timestamp: new Date().toISOString(),
      preferences: preferences,
      sessionId: this.analyticsData.id,
      sessionData: this.analyticsData  // Include full session data
    });
    
    // Save preferences to localStorage
    // this.saveUserPreferences(preferences);
    
    this.isInitialized = preferences.analytics;
    console.log('Preferences saved, analytics ' + (preferences.analytics ? 'enabled' : 'disabled'));
  }
  
  // ----------------------
  // Private API Methods
  // ----------------------
  
  private sendConsentDecisionToBackend(data: {
    action: 'accept' | 'decline' | 'customize';
    timestamp: string;
    preferences: UserPreferences;
    sessionId: string;
    sessionData: SessionData;
  }): void {
    if (typeof window === 'undefined') return;
    
    const payload = {
      eventType: 'consent_decision',
      data: data,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      referrer: document.referrer || 'direct',
      clientId: this.clientId,
      userAgent: navigator.userAgent
    };

    // Send to backend - UNCOMMENTED
    this.sendToBackend('/api/analytics/consent', payload);
  }

  private async sendToBackend(endpoint: string, data: any): Promise<void> {
    if (typeof window === 'undefined') return;

    const clientId = this.getClientId();
    if (clientId) {
      data.clientId = clientId;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Analytics-Version': '1.0',
          'X-Client-ID': this.clientId
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (err) {
      console.error('Failed to send consent data:', err);
    }
  }

  // ----------------------
  // Dashboard Data Fetching
  // ----------------------
  
  public async fetchPlatformAnalytics(): Promise<PlatformAnalytics | null> {
    if (typeof window === 'undefined') return null;
    
    try {
      const response = await fetch('/api/analytics/dashboard?type=platform', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': this.clientId
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Failed to fetch platform analytics:', err);
      return null;
    }
  }

  // ----------------------
  // Helper Methods
  // ----------------------
  private generateSessionId(): string {
    return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 11);
  }

  private detectDevice(): string {
    if (typeof navigator === 'undefined') return 'unknown';

    const ua = navigator.userAgent.toLowerCase();

    if (/mobile|iphone|ipod|android.*mobile|windows phone/.test(ua)) {
      return "mobile";
    }

    if (/ipad|tablet|android(?!.*mobile)/.test(ua)) {
      return "tablet";
    }

    return "desktop";
  }

  private detectBrowser(): string {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unknown';
    
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('chrome') && !ua.includes('edg')) return "Chrome";
    if (ua.includes('firefox')) return "Firefox";
    if (ua.includes('safari') && !ua.includes('chrome')) return "Safari";
    if (ua.includes('edg')) return "Edge";
    if (ua.includes('opera') || ua.includes('opr')) return "Opera";
    
    return "Unknown";
  }

  private detectOS(): string {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unknown';
    
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('win')) return "Windows";
    if (ua.includes('mac')) return "macOS";
    if (ua.includes('android')) return "Android";
    if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) return "iOS";
    if (ua.includes('linux')) return "Linux";
    
    return "Unknown";
  }

  private getScreenResolution(): string {
    if (typeof window === 'undefined') return '0x0';
    
    return `${window.screen.width}x${window.screen.height}`;
  }

  private getCountryFromIP(): string | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = timezone.split('/');
      
      if (parts.length > 1) {
        const region = parts[1].replace(/_/g, ' ');
        return region;
      }
      
      return timezone;
    } catch (err) {
      return null;
    }
  }

  public loadUserPreferences(): UserPreferences | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const saved = localStorage.getItem('userPreferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.warn('Could not load user preferences:', err);
    }
    
    return null;
  }

  // ----------------------
  // Reset & Cleanup
  // ----------------------
  public resetPlatformAnalytics(): void {
    AnalyticsCollector.platformAnalytics = {
      summary: {
        totalUsers: 0,
        analyticsAccepted: 0,
        analyticsDeclined: 0,
        acceptanceRate: 0
      },
      aggregatedData: {
        visitorStats: {
          totalVisitors: 0,
          dailyAverage: 0,
          weeklyGrowth: '0%',
          monthlyActive: 0
        },
        demographics: {
          topCountries: [],
          devices: {
            desktop: 0,
            mobile: 0,
            tablet: 0
          }
        }
      },
      updatedAt: new Date().toISOString()
    };
    // this.savePlatformAnalytics();
  }

  // ----------------------
  // Debug Helpers & Getters
  // ----------------------
  getSessionData(): SessionData | null {
    return this.analyticsData;
  }

  isAnalyticsEnabled(): boolean {
    return this.isInitialized;
  }

  getPlatformAnalyticsData(): PlatformAnalytics {
    return AnalyticsCollector.platformAnalytics;
  }

  getSessionId(): string | null {
    return this.analyticsData?.id || null;
  }
}

// Export singleton with lazy initialization
let analyticsCollectorInstance: AnalyticsCollector | null = null;

export function getAnalyticsCollector(): AnalyticsCollector {
  if (!analyticsCollectorInstance) {
    analyticsCollectorInstance = new AnalyticsCollector();
  }
  return analyticsCollectorInstance;
}

export default getAnalyticsCollector;
export type { 
  UserPreferences, 
  PlatformAnalytics,
  PlatformAnalyticsSummary,
  VisitorStats,
  DemographicsData
};